// ── Where each kind of record is worked ───────────────────────────────────────
// One table, shared by everything that can point at a record it does not own:
// the attention panels ("891 kg packed on K401-B001_L001") and global search.
// Keys are the `entity_type` / `type` values the backend emits — see the
// `_attn_row(...)` callers and `_SEARCH_SOURCES` in server.py.
//
// A type missing here renders as plain text rather than a link, which is right
// for rollup rows ("12 more pallets in the same state") since they name no single
// record — but a type that *does* name one and is missing here is a dead end, and
// that is the bug this table exists to prevent.
//
// `section` is the fallback: some records have no row of their own on the target
// page (a production order, a tank lot, a pallet the list has already consumed),
// so when the exact row cannot be found the panel that holds that kind of record
// is highlighted instead. The names match the `sectionAttrs(...)` on those panels.
export const RECORD_ROUTES = {
  sales_order: { path: '/sales', params: { tab: 'orders' }, section: 'sales-orders' },
  finished_good: { path: '/finished-goods', section: 'finished-goods' },
  cold_storage_inventory: {
    path: '/cold-storage/intake', params: { tab: 'inventory' }, section: 'cold-storage-inventory',
  },
  chamber: {
    path: '/cold-storage/intake', params: { tab: 'utilization' }, section: 'cold-storage-chambers',
  },
  reservation: {
    path: '/cold-storage/intake', params: { tab: 'inventory' }, section: 'cold-storage-inventory',
  },
  cold_storage_movement: { path: '/cold-storage/movements' },
  // Material still in the pool has no lot yet — it is worked from the Raw Material
  // Pool page, so that is where its alerts go.
  rm_line_item: { path: '/raw-material-pool', section: 'rm-pool' },
  procurement_lot: { path: '/procurement-batches', section: 'preprocessing-lots' },
  procurement_tank_lot: { path: '/procurement-batches', section: 'preprocessing-lots' },
  preprocessing_batch: { path: '/procurement-batches', section: 'preprocessing-lots' },
  // A handoff is material pre-processing pushed and production has not picked up;
  // the thing to do about it lives on the production floor, not on the push.
  // Production Entry is where a handoff is pulled into a lot and where the lot's
  // output is booked, so it is the screen that can actually resolve these rows.
  handoff: { path: '/production/entry' },
  production_handoff: { path: '/production/entry' },
  production_lot: { path: '/production/entry' },
  production_order: { path: '/production/entry' },
  // A packing line has no screen of its own — it is worked from its traveler's
  // packing stage, so the row opens Production Entry and lets the focus keys
  // (line code + traveler code) find whichever it can.
  production_detail_line: { path: '/production/entry' },
  purchase_invoice: { path: '/purchase-invoices', section: 'purchase-invoices' },
  // Buyers are an export-side master and are listed on Sales → Buyers. Parties
  // (farmers/suppliers) and Agents are the procurement-side masters, and that page
  // has no buyers tab at all — pointing here sent every buyer link to a screen that
  // could never hold the record.
  buyer: { path: '/sales', params: { tab: 'buyers' }, section: 'buyers' },
  agent: { path: '/agents', section: 'agents' },
  // Masters and fleet records: searchable because an operator looks for a party by
  // phone number and a vehicle by its plate far more often than by any code.
  party: { path: '/parties', params: { tab: 'parties' } },
  purchase_booking: { path: '/purchase-booking' },
  vehicle: { path: '/vehicle-compliance' },
  inventory_item: { path: '/inventory', params: { tab: 'stock' } },
  // Supervision rows name a queue or a stage rather than a business record, so
  // there is no row to hunt for — the link's whole job is to open the right tab.
  supervision_queue: { path: '/my-change-requests', params: { tab: 'approvals' } },
  supervision_request: { path: '/my-change-requests', params: { tab: 'approvals' } },
  supervision_stage: { path: '/masters', params: { tab: 'alerts', section: 'supervision' } },
};

/** Human label for a record type — used by search results and anywhere a row has
 *  to say what kind of thing it found. */
export const RECORD_TYPE_LABELS = {
  rm_line_item: 'RM pool row',
  // Plant words (config/terminology.js): a production day holds pond lots; a lot is
  // graded into batches. The type keys are storage names and stay as they are.
  procurement_lot: 'production day',
  procurement_tank_lot: 'Lot',
  preprocessing_batch: 'production day',
  production_lot: 'Lot (production)',
  production_order: 'Production order',
  production_detail_line: 'Batch / line',
  production_handoff: 'Handoff',
  handoff: 'Handoff',
  finished_good: 'Finished good',
  cold_storage_inventory: 'Cold store',
  cold_storage_movement: 'Movement',
  chamber: 'Chamber',
  reservation: 'Reservation',
  sales_order: 'Sales order',
  purchase_invoice: 'Purchase invoice',
  buyer: 'Buyer',
  agent: 'Agent',
  party: 'Party',
  purchase_booking: 'Booking',
  vehicle: 'Vehicle',
  inventory_item: 'Item',
  supervision_queue: 'Review queue',
  supervision_request: 'Held change',
  supervision_stage: 'Stage supervision',
};

/** The record a hit/alert points at, in the shape `useOpenRecord` wants. Returns
 *  null when the type has no screen — the caller renders text instead of a link. */
export function routeTarget(type, id, labels = []) {
  const route = RECORD_ROUTES[type];
  if (!route || !id) return null;
  return {
    path: route.path,
    params: route.params,
    section: route.section,
    id,
    label: labels.filter(Boolean),
  };
}

export default RECORD_ROUTES;
